/**
 * BreachDrillPage
 * Route: /admin/breach/drill
 *
 * Breach simulation drill execution page (dev/staging only).
 * Tests detection, containment, and notification workflows.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Play,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Zap,
  Shield,
  FileText,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { runBreachDrill, listBreachDrills } from "@/services/dpdpApi";

const SCENARIO_TYPES = [
  { id: "unauthorized_access", label: "Unauthorized Access", description: "Simulate account takeover or brute-force attack" },
  { id: "data_leak", label: "Data Leak", description: "Simulate unintended data exposure" },
  { id: "system_breach", label: "System Breach", description: "Simulate infrastructure vulnerability exploitation" },
  { id: "insider_threat", label: "Insider Threat", description: "Simulate malicious insider activity" },
  { id: "third_party", label: "Third-Party Breach", description: "Simulate vendor-related breach" },
  { id: "physical", label: "Physical Breach", description: "Simulate physical security incident" },
  { id: "other", label: "Other", description: "Custom breach scenario" },
];

const SEVERITY_LEVELS = [
  { id: "low", label: "Low", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200", description: "Minimal impact" },
  { id: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200", description: "Moderate scope" },
  { id: "high", label: "High", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200", description: "Significant impact" },
  { id: "critical", label: "Critical", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200", description: "Widespread exposure" },
];

export default function BreachDrillPage() {
  const navigate = useNavigate();
  const [drillName, setDrillName] = useState("");
  const [scenarioType, setScenarioType] = useState("");
  const [severity, setSeverity] = useState("");
  const [affectedCategories, setAffectedCategories] = useState([]);
  const [estimatedUsers, setEstimatedUsers] = useState("10");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [drillHistory, setDrillHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useState(() => {
    listBreachDrills()
      .then((data) => setDrillHistory(data?.items || []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  });

  const toggleCategory = (cat) => {
    setAffectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleRunDrill = async (e) => {
    e.preventDefault();
    if (!drillName || !scenarioType || !severity) {
      toast.error("Please fill all required fields");
      return;
    }

    setRunning(true);
    try {
      const data = await runBreachDrill({
        drill_name: drillName,
        scenario_type: scenarioType,
        simulated_severity: severity,
        affected_data_categories: affectedCategories,
        estimated_users: parseInt(estimatedUsers) || 10,
      });
      setResult(data);
      toast.success("Breach drill completed successfully");
    } catch (err) {
      if (err.message && err.message.includes("development") || err.message && err.message.includes("staging")) {
        toast.error("Breach drills are only available in development/staging environments");
      } else {
        toast.error(err.message || "Failed to run breach drill");
      }
    } finally {
      setRunning(false);
    }
  };

  const AUTOMATION_ICONS = {
    "Detection:": <Shield className="h-4 w-4 text-blue-400" />,
    "Alert": <AlertTriangle className="h-4 w-4 text-amber-400" />,
    "Automatic": <Zap className="h-4 w-4 text-yellow-400" />,
    "Simulated:": <Activity className="h-4 w-4 text-green-400" />,
    "Containment": <CheckCircle className="h-4 w-4 text-orange-400" />,
    "Post": <FileText className="h-4 w-4 text-purple-400" />,
  };

  const getIconFor = (text) => {
    for (const [prefix, icon] of Object.entries(AUTOMATION_ICONS)) {
      if (text.includes(prefix)) return icon;
    }
    return <CheckCircle className="h-4 w-4 text-green-400" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950/20 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-display text-lg font-bold text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-400" />
              Breach Simulation Drill
            </h1>
            <p className="text-xs text-white/50">Test your detection, containment & notification workflows</p>
          </div>
          <Badge variant="outline" className="ml-auto border-purple-500/50 text-purple-400">
            Dev / Staging
          </Badge>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Warning */}
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              Simulation Environment Only
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Breach drills are for testing purposes only. No real notifications are sent to the
              Data Protection Board or users during simulation.
            </p>
          </div>
        </div>

        {/* Drill Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Play className="h-4 w-4 text-purple-400" />
              Configure Simulation
            </CardTitle>
            <CardDescription>Set up the breach scenario parameters</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRunDrill} className="space-y-6">
              <div>
                <Label className="text-white/70">Drill Name *</Label>
                <Input
                  value={drillName}
                  onChange={(e) => setDrillName(e.target.value)}
                  placeholder="e.g., Q2 2026 Breach Response Test"
                  className="mt-1 bg-white/10 border-white/20 text-white"
                />
              </div>

              <div>
                <Label className="text-white/70">Scenario Type *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {SCENARIO_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setScenarioType(type.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        scenarioType === type.id
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-white/10 bg-white/5 hover:border-white/30"
                      }`}
                    >
                      <p className="font-medium text-sm text-white">{type.label}</p>
                      <p className="text-xs text-white/40">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white/70">Simulated Severity *</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {SEVERITY_LEVELS.map((level) => (
                    <button
                      key={level.id}
                      type="button"
                      onClick={() => setSeverity(level.id)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        severity === level.id
                          ? "border-white bg-white/10"
                          : "border-white/10 bg-white/5 hover:border-white/30"
                      }`}
                    >
                      <p className="text-sm font-bold text-white">{level.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white/70">Simulated Affected Categories</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["name", "email", "phone", "password", "financial", "health", "location"].map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                        affectedCategories.includes(cat)
                          ? "border-purple-500 bg-purple-500/20 text-purple-300"
                          : "border-white/10 bg-white/5 text-white/60 hover:border-white/30"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white/70">Simulated Affected Users</Label>
                <Input
                  type="number"
                  min="1"
                  value={estimatedUsers}
                  onChange={(e) => setEstimatedUsers(e.target.value)}
                  className="mt-1 bg-white/10 border-white/20 text-white w-40"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-base"
                disabled={running || !drillName || !scenarioType || !severity}
              >
                {running ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Running Simulation...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Breach Simulation
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Drill Results */}
        {result && (
          <Card className="border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-400" />
                Drill Results: {result.name}
              </CardTitle>
              <CardDescription>
                {result.simulated_severity.toUpperCase()} severity — {result.scenario_type.replace(/_/g, " ")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Automated responses */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-400" />
                  Triggered Automated Responses ({result.triggered_automations.length})
                </h3>
                <div className="space-y-2">
                  {result.triggered_automations.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2.5">
                      {getIconFor(item)}
                      <span className="text-sm text-white/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommendations */}
              {result.recommendations.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-400" />
                    Recommendations
                  </h3>
                  <div className="space-y-2">
                    {result.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3">
                        <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-amber-200">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{result.triggered_automations.length}</div>
                  <div className="text-xs text-white/50">Automations Triggered</div>
                </div>
                <div className="bg-white/5 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-4 w-4 text-green-400" />
                    <span className="text-2xl font-bold text-white">{result.user_notifications_sent}</span>
                  </div>
                  <div className="text-xs text-white/50">Users Notified (simulated)</div>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setResult(null);
                  setDrillName("");
                  setScenarioType("");
                  setSeverity("");
                  setAffectedCategories([]);
                }}
              >
                Run Another Drill
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
